import { useState } from 'react';
import { FitPassportForm } from '@/components/FitPassportForm';
import { useFitPassport } from '@/hooks/useFitPassport';
import { emptyPassport } from '@/types/passport';

function formatUpdatedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export function FitPassportPage() {
  const { passport, save, remove } = useFitPassport();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [savedJustNow, setSavedJustNow] = useState(false);

  function handleSave(next: Parameters<typeof save>[0]) {
    save(next);
    setSavedJustNow(true);
    setTimeout(() => setSavedJustNow(false), 3000);
  }

  function handleExport() {
    if (!passport) return;
    const blob = new Blob([JSON.stringify(passport, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'fitmap-passport.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-ink">FitMap Passport</h2>
        <p className="mt-1 text-sm text-muted">
          Save your measurements once and reuse them across comparisons instead of retyping them. Your Passport
          enhances a comparison — it never replaces the verified brand-chart measurements the recommendation is
          actually built from.
        </p>
        <p className="mt-2 text-xs text-muted">
          Stored only on this device, in this browser. It isn't uploaded anywhere unless you export it yourself, and
          there's no account for it to sync to.
        </p>
      </div>

      {passport && (
        <p className="text-xs text-muted" role="status">
          Last updated {formatUpdatedAt(passport.updatedAt)}
        </p>
      )}
      {savedJustNow && (
        <p role="status" className="rounded-xl bg-brand/10 p-3 text-sm text-brand">
          Saved.
        </p>
      )}

      <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-card">
        <FitPassportForm initial={passport ?? emptyPassport()} onSave={handleSave} />
      </div>

      {passport && (
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleExport}
            className="min-h-[44px] rounded-xl border border-stone-200 bg-white px-4 text-sm font-semibold text-ink hover:border-brand/40"
          >
            Export as JSON
          </button>
          {!confirmingDelete ? (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="min-h-[44px] rounded-xl border border-stone-200 bg-white px-4 text-sm font-semibold text-danger hover:border-danger/40"
            >
              Delete Passport
            </button>
          ) : (
            <div className="flex min-h-[44px] items-center gap-2 rounded-xl border border-danger/30 bg-danger/5 px-4 text-sm">
              <span className="text-danger">Delete permanently?</span>
              <button
                type="button"
                onClick={() => {
                  remove();
                  setConfirmingDelete(false);
                }}
                className="font-semibold text-danger underline"
              >
                Yes, delete
              </button>
              <button type="button" onClick={() => setConfirmingDelete(false)} className="text-muted underline">
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
